import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { LocalitatiService } from '../localitati.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { JudeteService } from '../../judete/judete.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { LocalitateUpdate } from '../localitati.interface';

@Component({
    selector: 'app-edit-localitati',
    templateUrl: './edit-localitati.component.html',
    styleUrls: ['./edit-localitati.component.scss']
})
export class EditLocalitatiComponent implements OnInit, OnDestroy {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    tari: Generic[] = this.data.tari;
    judete: Generic[] = [];

    private editDestroy$ = new Subject<void>();

    // Filtered fields for search
    filteredTari: any[] = this.data.tari;
    tariControl = new FormControl();

    filteredJudete: any[] = [];
    judeteControl = new FormControl();

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.data.name, Validators.required),
        id_country: new FormControl(this.data.data.id_country, Validators.required),
        id_judet: new FormControl(this.data.data.id_judet, Validators.required)
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<EditLocalitatiComponent>,
        private localitatiService: LocalitatiService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private judeteService: JudeteService
    ) {
        this.processToken();

        this.form.get('id_country')?.disable();
    }

    ngOnInit() {
        this.renderFieldsSearch();

        if (this.data.data.id_country) {
            this.loadJudete(this.data.data.country_id);
        }
    }

    ngOnDestroy(): void {
        this.editDestroy$.next();
        this.editDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.localitatiService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.tariControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTari = this.tari;
                    return of(this.tari);
                }
                return this.filterTari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.judeteControl.valueChanges.pipe(
            takeUntil(this.editDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredJudete = this.judete;
                    return of(this.judete);
                }
                return this.filterJudete(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredJudete = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterTari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tari.filter(tara =>
            this.normalizeString(tara.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTaraSelectClosed() {
        const selectedValue = this.tariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTari.some(tara =>
                tara.id === this.form.get('id_country')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tariControl.setValue(null);
                this.filteredTari = this.tari;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onTaraChange(taraId: number): void {
        this.form.controls['id_judet'].patchValue(-1);

        if (taraId == -1) {
            this.judete = [];
            this.filteredJudete = [];
            return
        }

        if (taraId) {
            this.loadJudete(taraId);
        }
    }

    loadJudete(taraId: number) {
        this.isLoading = true;

        let judeteObservable$ = taraId
            ? this.judeteService.getJudeteActiveByTari(taraId)
            : this.judeteService.getJudeteActive();

        judeteObservable$.pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (judete) => {
                this.judete = [...judete];
                this.filteredJudete = [...judete];
            },
            error: (err) => {
                console.log(err);
                this.filteredJudete = [];
            }
        });
    }

    onJudeteSelectClosed() {
        const selectedValue = this.judeteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredJudete.some(judet =>
                judet.id === this.form.get('id_judet')?.value
            );

            if (!isSelectedInFilteredList) {
                this.judeteControl.setValue(null);
                this.filteredJudete = this.judete;
                this.editDestroy$.next();
                this.editDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterJudete(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.judete.filter(judet =>
            this.normalizeString(judet.name).includes(normalizedSearchTerm)
        ));
    }

    submitForm() {
        this.isLoading = true;

        const updatedTaraData: LocalitateUpdate = {
            name: this.form.get('name')?.value,
            id_country: this.form.get('id_country')?.value,
            id_judet: this.form.get('id_judet')?.value,
            id: this.data.data.id,
        };

        this.localitatiService
            .updateLocalitate(updatedTaraData)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let errorMessage =
                        environment.config.customNotifications.generalMessages.error;
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        errorMessage,
                        this.errorIcon
                    );
                },
            });
    }
}
